import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Check } from "lucide-react";
import { FadeUp, Overline, MaskedLines } from "../components/Reveal";
import Seo from "../components/Seo";
import { LINKS, IMAGES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATS = [
  { value: "$40,000", label: "Franchise Fee" },
  { value: "$150K – $250K", label: "Investment Range" },
  { value: "5%", label: "Royalty" },
  { value: "1%", label: "Brand Fund" },
];

const REASONS = [
  { title: "Uncompromising Quality", body: "The finest tea leaves and ingredients sourced from Taiwan, China and India — with stringent quality control guaranteeing consistency in every cup." },
  { title: "Continuous Innovation", body: "New flavors, brewing techniques and packaging innovations keep franchisees at the forefront of the ever-evolving tea industry." },
  { title: "Wide Product Availability", body: "An extensive range of blends backed by an efficient supply chain — so you can confidently meet demand and keep customers coming back." },
  { title: "Strong Brand Recognition", body: "Ranked #47 on Entrepreneur's Top 150 New Franchises — a trusted, beloved brand that attracts customers from day one." },
  { title: "Global Presence", body: "A vast network of franchisees across the US, with expansion underway in India, Canada, Mexico and the Middle East." },
  { title: "Training & Support", body: "A seven-day training program, complete operations documentation, and ongoing support in marketing, inventory and recruitment." },
];

const inputClass =
  "w-full bg-transparent border-0 border-b border-white/20 focus:border-brand rounded-none px-0 py-4 text-paper placeholder:text-white/30 font-body font-light outline-none transition-colors duration-300 focus:ring-0";

const labelClass = "text-[11px] uppercase tracking-[0.2em] text-white/40 font-body";

export default function Franchise() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", market: "", capital: "", timeframe: "", experience: "", message: "",
  });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/franchise`, form);
      toast.success("Franchise inquiry received. The ThirsTea team will reach out shortly.");
      setForm({ name: "", email: "", phone: "", market: "", capital: "", timeframe: "", experience: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="franchise-page" className="pt-40">
      <Seo
        title="Franchise With ThirsTea USA — Gurpreet Paul"
        description="Own a ThirsTea USA boba tea franchise. $40,000 franchise fee, $150K–$250K investment range, comprehensive training and ongoing support. Apply for franchising with CEO Gurpreet Paul's team."
      />
      <header className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-8">
          <FadeUp>
            <Overline>Franchise With ThirsTea USA</Overline>
          </FadeUp>
          <MaskedLines
            lines={["Build your", "own legacy."]}
            className="font-display font-medium tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl text-paper mt-6"
            delay={0.15}
          />
          <FadeUp delay={0.35}>
            <p className="mt-8 max-w-2xl text-white/55 text-base md:text-lg leading-relaxed font-body font-light">
              Join one of America's fastest-growing boba tea franchise brands — a proven model with
              comprehensive training, ongoing support, and a brand built to stand out in any market.
            </p>
          </FadeUp>
        </div>
        <FadeUp delay={0.3} className="lg:col-span-4 flex lg:justify-end">
          <a
            href={LINKS.thirstea}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="franchise-logo-link"
            className="block hover:scale-[1.05] transition-transform duration-500"
          >
            <img
              src={IMAGES.thirsteaLogo}
              alt="ThirsTea USA by Brain Tune Cafe — official logo"
              data-testid="franchise-logo"
              className="w-44 md:w-56"
            />
          </a>
        </FadeUp>
      </header>

      <section data-testid="franchise-stats" className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-2 lg:grid-cols-4">
          {STATS.map((stat, i) => (
            <FadeUp key={stat.label} delay={i * 0.06}>
              <div
                data-testid={`franchise-stat-${i + 1}`}
                className="py-14 px-6 border-l border-white/10 first:border-l-0"
              >
                <p className="font-display font-medium tracking-tight text-3xl md:text-4xl text-brand">{stat.value}</p>
                <p className="mt-3 text-[11px] uppercase tracking-[0.25em] text-white/40 font-body">{stat.label}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section data-testid="franchise-why" className="border-t border-white/10 bg-[#0D0D0F]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-32">
          <FadeUp>
            <Overline>Why ThirsTea</Overline>
            <h2 className="font-display font-medium tracking-tighter leading-[0.95] text-4xl sm:text-5xl text-paper mt-6 max-w-2xl">
              A system built before the first store opened
            </h2>
          </FadeUp>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {REASONS.map((reason, i) => (
              <FadeUp key={reason.title} delay={i * 0.05}>
                <div
                  data-testid={`franchise-reason-${i + 1}`}
                  className="group bg-[#0A0A0A] p-10 h-full hover:bg-[#141416] transition-colors duration-500"
                >
                  <span className="font-display font-light text-4xl text-stroke leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-6 font-display font-medium tracking-tight text-2xl text-paper group-hover:text-brand transition-colors duration-500">
                    {reason.title}
                  </h3>
                  <p className="mt-4 text-white/50 text-sm leading-relaxed font-body font-light">{reason.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="franchise-apply" className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-32 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <FadeUp className="lg:col-span-5">
            <Overline>Start the Conversation</Overline>
            <h2 className="font-display font-medium tracking-tighter leading-[0.95] text-4xl sm:text-5xl text-paper mt-6">
              Franchise inquiry
            </h2>
            <p className="mt-6 text-white/55 text-base leading-relaxed font-body font-light max-w-md">
              Tell us about yourself and the market you have in mind. Every inquiry goes directly to
              Gurpreet's team at ThirsTea USA — expect a personal response, not an autoresponder.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Seven-day comprehensive training program",
                "Complete operations documentation",
                "Ongoing marketing & supply chain support",
                "Territories available across the US and internationally",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-white/60 text-sm font-body font-light">
                  <Check size={16} className="text-brand mt-0.5 shrink-0" /> {item}
                </li>
              ))}
            </ul>
            <a
              href="https://thirstea.com/franchise/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="franchise-thirstea-link"
              className="mt-10 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-white/40 hover:text-brand transition-colors duration-300 font-body"
            >
              Explore the franchise program on thirstea.com <ArrowUpRight size={14} />
            </a>
            <div className="mt-12 overflow-hidden border border-white/10 hidden lg:block">
              <img
                src={IMAGES.portraitGrey}
                alt="ThirsTea USA franchise"
                loading="lazy"
                data-testid="franchise-image"
                className="w-full h-72 object-cover hover:scale-[1.03] transition-transform duration-[1.2s]"
              />
            </div>
          </FadeUp>

          <FadeUp delay={0.2} className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-testid="franchise-form"
              className="border border-white/10 bg-[#111113] p-8 md:p-12"
            >
              <p className="text-[11px] uppercase tracking-[0.25em] text-brand font-body mb-10">Apply for Franchising</p>
              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label htmlFor="f-name" className={labelClass}>Full Name *</label>
                    <input id="f-name" name="name" required value={form.name} onChange={handleChange} placeholder="Your full name" data-testid="franchise-form-name" className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="f-email" className={labelClass}>Email *</label>
                    <input id="f-email" name="email" type="email" required value={form.email} onChange={handleChange} placeholder="you@example.com" data-testid="franchise-form-email" className={inputClass} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label htmlFor="f-phone" className={labelClass}>Phone</label>
                    <input id="f-phone" name="phone" value={form.phone} onChange={handleChange} placeholder="(555) 000-0000" data-testid="franchise-form-phone" className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="f-market" className={labelClass}>Market of Interest</label>
                    <input id="f-market" name="market" value={form.market} onChange={handleChange} placeholder="City / State / Country" data-testid="franchise-form-market" className={inputClass} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label htmlFor="f-capital" className={labelClass}>Liquid Capital Available</label>
                    <select id="f-capital" name="capital" value={form.capital} onChange={handleChange} data-testid="franchise-form-capital" className={`${inputClass} bg-[#111113] cursor-pointer`}>
                      <option value="">Select a range</option>
                      <option value="Under $100,000">Under $100,000</option>
                      <option value="$100,000 – $150,000">$100,000 – $150,000</option>
                      <option value="$150,000 – $250,000">$150,000 – $250,000</option>
                      <option value="$250,000+">$250,000+</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="f-timeframe" className={labelClass}>Desired Timeframe</label>
                    <select id="f-timeframe" name="timeframe" value={form.timeframe} onChange={handleChange} data-testid="franchise-form-timeframe" className={`${inputClass} bg-[#111113] cursor-pointer`}>
                      <option value="">Select a timeframe</option>
                      <option value="Immediately">Immediately</option>
                      <option value="Within 3 months">Within 3 months</option>
                      <option value="3 – 6 months">3 – 6 months</option>
                      <option value="6+ months">6+ months</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="f-experience" className={labelClass}>Business / Restaurant Experience</label>
                  <textarea id="f-experience" name="experience" rows={3} value={form.experience} onChange={handleChange} placeholder="Briefly describe your background…" data-testid="franchise-form-experience" className={`${inputClass} resize-none`} />
                </div>
                <div>
                  <label htmlFor="f-message" className={labelClass}>Anything Else</label>
                  <textarea id="f-message" name="message" rows={3} value={form.message} onChange={handleChange} placeholder="Questions, goals, or anything we should know…" data-testid="franchise-form-message" className={`${inputClass} resize-none`} />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="franchise-form-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 text-[12px] uppercase tracking-[0.15em] text-white hover:bg-brand-dark transition-colors duration-300 disabled:opacity-60"
                >
                  {sending ? <Loader2 size={15} className="animate-spin" /> : null}
                  {sending ? "Submitting…" : "Submit Franchise Inquiry"}
                </button>
              </div>
            </form>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
